import { useEffect, useRef } from 'react';

// Genera tonos sinteticos con Web Audio API. Sin archivos externos.
// kind: 'incoming' (campanazo de llamada entrante) | 'outgoing' (dial tone continuo)

export default function useRingTone(active, kind = 'incoming') {
    const ctxRef = useRef(null);
    const intervalRef = useRef(null);
    const stoppedRef = useRef(false);

    useEffect(() => {
        if (!active) return undefined;

        stoppedRef.current = false;
        let audioCtx;
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return undefined;
            audioCtx = new AudioContextClass();
            ctxRef.current = audioCtx;
            // Puede nacer suspendido (politica de autoplay): sin esto no suena nada
            if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
        } catch {
            return undefined;
        }

        const playRingBurst = () => {
            if (stoppedRef.current || !audioCtx) return;
            const now = audioCtx.currentTime;
            // Dos osciladores (acorde menor) para sonar como ringer
            [880, 660].forEach((freq) => {
                const osc = audioCtx.createOscillator();
                const gain = audioCtx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0, now);
                gain.gain.linearRampToValueAtTime(0.18, now + 0.05);
                gain.gain.linearRampToValueAtTime(0.18, now + 0.85);
                gain.gain.linearRampToValueAtTime(0, now + 0.95);
                osc.connect(gain);
                gain.connect(audioCtx.destination);
                osc.start(now);
                osc.stop(now + 1.0);
            });
        };

        const playDialTone = () => {
            if (stoppedRef.current || !audioCtx) return;
            const now = audioCtx.currentTime;
            // Beep corto, suave, repetido
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.type = 'sine';
            osc.frequency.value = 480;
            gain.gain.setValueAtTime(0, now);
            gain.gain.linearRampToValueAtTime(0.12, now + 0.05);
            gain.gain.linearRampToValueAtTime(0.12, now + 0.45);
            gain.gain.linearRampToValueAtTime(0, now + 0.55);
            osc.connect(gain);
            gain.connect(audioCtx.destination);
            osc.start(now);
            osc.stop(now + 0.6);
        };

        if (kind === 'incoming') {
            playRingBurst();
            intervalRef.current = setInterval(playRingBurst, 2200);
        } else {
            playDialTone();
            intervalRef.current = setInterval(playDialTone, 1800);
        }

        return () => {
            stoppedRef.current = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
            if (audioCtx) {
                try { audioCtx.close(); } catch { /* noop */ }
            }
            ctxRef.current = null;
        };
    }, [active, kind]);
}
